<template>
  <div class="main-content-container overflow-hidden">
    <PageTitle pageTitle="Penandatangan Protokol" subTitle="Pengesahan Dokumen" />

    <div class="card bg-white border-0 rounded-3 mb-4">
      <div class="card-body p-0">
        <!-- Top Toolbar -->
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 p-4 border-bottom">
          <div>
            <h5 class="mb-1 fs-16 fw-semibold text-dark">Daftar Pejabat Penandatangan Protokol</h5>
            <p class="text-muted fs-13 mb-0">Kelola nama, jabatan, dan gambar tanda tangan digital yang digunakan pada pengesahan sertifikat protokol resmi (RC-SIM, RC-GRM, RC-AI).</p>
          </div>
          <div class="d-flex align-items-center gap-3">
            <div class="position-relative table-src-form me-0">
              <input type="text" class="form-control" placeholder="Cari nama atau jabatan..." v-model="searchQuery" />
              <i class="material-symbols-outlined position-absolute top-50 start-0 translate-middle-y">search</i>
            </div>
            <button type="button" @click="openCreateModal" class="btn btn-primary py-2 px-3 fs-14 fw-medium rounded-3 d-flex align-items-center gap-1">
              <i class="material-symbols-outlined fs-18">add</i>
              <span>Tambah Penandatangan</span>
            </button>
          </div>
        </div>

        <!-- Info Notice -->
        <div class="p-3 bg-light-subtle border-bottom fs-13 text-muted d-flex align-items-center gap-2">
          <i class="material-symbols-outlined fs-18 text-primary">verified_user</i>
          <span>
            <strong>Integritas Dokumen Legal:</strong> Penandatangan berstatus <strong>Default</strong> akan otomatis dipilih saat form protokol dibuka. Perubahan data di sini tidak akan merusak snapshot tanda tangan pada protokol lama yang sudah terbit.
          </span>
        </div>

        <!-- Signatories Table -->
        <div class="default-table-area style-two default-table-width">
          <div class="table-responsive">
            <table class="table align-middle mb-0">
              <thead>
                <tr>
                  <th scope="col" style="width: 50px;">#</th>
                  <th scope="col" style="min-width: 220px;">Pejabat Penandatangan</th>
                  <th scope="col" style="min-width: 170px;">Tanda Tangan</th>
                  <th scope="col" style="min-width: 140px;">Status Default</th>
                  <th scope="col" style="min-width: 120px;">Status Aktif</th>
                  <th scope="col" style="min-width: 140px;">Dibuat</th>
                  <th scope="col" class="text-end" style="min-width: 120px;">Aksi</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td colspan="7" class="text-center py-5 text-muted">
                    <div class="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                    Memuat data penandatangan...
                  </td>
                </tr>
                <tr v-else-if="filteredSignatories.length === 0">
                  <td colspan="7" class="text-center py-5 text-muted">
                    Tidak ada penandatangan protokol yang ditemukan.
                  </td>
                </tr>
                <tr v-else v-for="(item, index) in filteredSignatories" :key="item.id">
                  <td class="text-muted fs-13">{{ index + 1 }}</td>
                  <td>
                    <div class="d-flex align-items-center gap-2">
                      <div class="rounded-circle bg-primary bg-opacity-10 text-primary d-flex align-items-center justify-content-center" style="width: 38px; height: 38px; flex-shrink: 0;">
                        <i class="material-symbols-outlined fs-20">badge</i>
                      </div>
                      <div>
                        <div class="fw-bold text-dark fs-14">{{ item.name }}</div>
                        <div class="text-muted fs-12">{{ item.position }}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div v-if="item.signature_image_url || item.signature_image" class="signature-thumb-box" @click="zoomSignature(item)" title="Klik untuk memperbesar">
                      <img :src="getFileUrl(item.signature_image_url || item.signature_image)" alt="Tanda Tangan" class="signature-thumb-img" />
                      <span class="zoom-badge">
                        <i class="material-symbols-outlined fs-14">zoom_in</i>
                      </span>
                    </div>
                    <span v-else class="text-muted fs-12 fst-italic">Tanpa gambar</span>
                  </td>
                  <td>
                    <span v-if="item.is_default" class="badge bg-warning bg-opacity-20 text-warning-emphasis fs-12 px-2 py-1 d-inline-flex align-items-center gap-1">
                      <i class="material-symbols-outlined fs-14">star</i>
                      <span>Default</span>
                    </span>
                    <button
                      v-else
                      type="button"
                      @click="handleSetDefault(item)"
                      class="btn btn-sm btn-outline-secondary fs-11 py-1 px-2"
                      :disabled="actionLoadingId === item.id"
                    >
                      <span v-if="actionLoadingId === item.id" class="spinner-border spinner-border-sm me-1"></span>
                      Jadikan Default
                    </button>
                  </td>
                  <td>
                    <button
                      type="button"
                      @click="handleToggleActive(item)"
                      class="btn btn-sm border-0 px-2 py-1 fs-12 d-inline-flex align-items-center gap-1"
                      :class="item.is_active ? 'badge bg-success bg-opacity-10 text-success' : 'badge bg-danger bg-opacity-10 text-danger'"
                      :disabled="actionLoadingId === item.id"
                    >
                      <i class="material-symbols-outlined fs-14">{{ item.is_active ? 'check_circle' : 'cancel' }}</i>
                      <span>{{ item.is_active ? 'Aktif' : 'Non-Aktif' }}</span>
                    </button>
                  </td>
                  <td class="fs-12 text-muted">
                    {{ formatDate(item.created_at) }}
                  </td>
                  <td class="text-end">
                    <div class="d-inline-flex gap-1">
                      <button
                        type="button"
                        @click="openEditModal(item)"
                        class="btn btn-sm btn-outline-primary p-1 d-inline-flex align-items-center"
                        title="Edit Penandatangan"
                      >
                        <i class="material-symbols-outlined fs-16">edit</i>
                      </button>
                      <button
                        type="button"
                        @click="handleDelete(item)"
                        class="btn btn-sm btn-outline-danger p-1 d-inline-flex align-items-center"
                        title="Hapus / Arsipkan"
                        :disabled="item.is_default"
                      >
                        <i class="material-symbols-outlined fs-16">delete</i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Form (Tambah / Edit Penandatangan) -->
    <div v-if="showModal" class="custom-modal-backdrop" @click.self="closeModal">
      <div class="custom-modal-card card border-0 shadow-lg">
        <div class="card-header bg-white d-flex justify-content-between align-items-center py-3 px-4 border-bottom">
          <h5 class="mb-0 fs-16 fw-semibold text-dark d-flex align-items-center gap-2">
            <i class="material-symbols-outlined fs-20 text-primary">{{ isEditing ? 'edit_square' : 'person_add' }}</i>
            <span>{{ isEditing ? 'Edit Penandatangan' : 'Tambah Penandatangan Baru' }}</span>
          </h5>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>

        <form @submit.prevent="saveSignatory">
          <div class="card-body p-4">
            <!-- Nama -->
            <div class="mb-3">
              <label class="form-label fs-13 fw-semibold text-dark">
                Nama Lengkap & Gelar <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                class="form-control"
                v-model="form.name"
                placeholder="cth: Yulfitri, SKM., MPH"
                required
              />
              <small class="text-muted">Nama yang tercantum resmi di atas garis tanda tangan.</small>
            </div>

            <!-- Jabatan -->
            <div class="mb-3">
              <label class="form-label fs-13 fw-semibold text-dark">
                Jabatan Resmi <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                class="form-control"
                v-model="form.position"
                placeholder="cth: Direktur PT Raya Berdikari Nusantara"
                required
              />
              <small class="text-muted">Jabatan atau kapasitas pengesahan dalam sertifikat protokol.</small>
            </div>

            <!-- Upload Gambar Tanda Tangan -->
            <div class="mb-3">
              <label class="form-label fs-13 fw-semibold text-dark">
                File Gambar Tanda Tangan <span v-if="!isEditing" class="text-danger">*</span>
              </label>
              <input
                type="file"
                class="form-control"
                ref="fileInputRef"
                accept="image/png, image/jpeg, image/jpg, image/webp, image/svg+xml"
                @change="handleFileChange"
                :required="!isEditing && !form.signature_file"
              />
              <small class="text-muted d-block mt-1">
                Format: <strong>PNG (latar belakang transparan direkomendasikan)</strong> atau JPG/WebP. Maks. 2MB.
              </small>

              <!-- Preview Signature Box -->
              <div v-if="previewUrl" class="mt-3 p-3 border rounded-3 bg-light text-center">
                <div class="text-muted fs-11 text-uppercase fw-semibold mb-2">Simulasi Tampilan Pada Sertifikat Protokol:</div>
                <div class="signature-preview-card p-3 mx-auto bg-white rounded border d-inline-block shadow-sm">
                  <div class="fs-12 text-primary fw-bold mb-1">Pengesahan {{ form.position || 'Jabatan' }}</div>
                  <div class="signature-render-wrap my-2">
                    <img :src="getFileUrl(previewUrl)" alt="Preview Tanda Tangan" class="signature-render-img" />
                  </div>
                  <div class="fs-13 fw-bold text-dark text-decoration-underline">{{ form.name || 'Nama Penandatangan' }}</div>
                  <div class="fs-11 text-muted">{{ form.position || 'Jabatan Penandatangan' }}</div>
                </div>
              </div>
            </div>

            <!-- Checkbox Default & Aktif -->
            <div class="row mt-3 pt-3 border-top">
              <div class="col-md-6 mb-2">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" id="signatoryActiveCheck" v-model="form.is_active" />
                  <label class="form-check-label fs-13 fw-semibold" for="signatoryActiveCheck">Status Aktif</label>
                </div>
                <small class="text-muted fs-11">Hanya penandatangan aktif yang muncul di pilihan form protokol.</small>
              </div>

              <div class="col-md-6 mb-2">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" id="signatoryDefaultCheck" v-model="form.is_default" />
                  <label class="form-check-label fs-13 fw-semibold" for="signatoryDefaultCheck">Jadikan Default</label>
                </div>
                <small class="text-muted fs-11">Otomatis dipilih secara default saat admin membuat protokol baru.</small>
              </div>
            </div>
          </div>

          <div class="card-footer bg-light d-flex justify-content-end gap-2 py-3 px-4">
            <button type="button" class="btn btn-secondary px-3" @click="closeModal" :disabled="saving">
              Batal
            </button>
            <button type="submit" class="btn btn-primary px-4 d-flex align-items-center gap-2" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm"></span>
              <i v-else class="material-symbols-outlined fs-18">save</i>
              <span>{{ isEditing ? 'Simpan Perubahan' : 'Simpan Penandatangan' }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Zoom Preview Modal -->
    <div v-if="zoomItem" class="custom-modal-backdrop" @click="zoomItem = null">
      <div class="card border-0 shadow-lg p-4 text-center bg-white" style="max-width: 480px; width: 90%;" @click.stop>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="mb-0 fw-bold text-dark">Tanda Tangan Digital</h6>
          <button type="button" class="btn-close" @click="zoomItem = null"></button>
        </div>
        <div class="p-4 border rounded-3 bg-light d-flex align-items-center justify-content-center" style="min-height: 180px;">
          <img :src="getFileUrl(zoomItem.signature_image_url || zoomItem.signature_image)" alt="Tanda Tangan" style="max-height: 140px; max-width: 100%; object-fit: contain;" />
        </div>
        <div class="mt-3 text-center">
          <div class="fw-bold fs-15 text-dark">{{ zoomItem.name }}</div>
          <div class="text-muted fs-13">{{ zoomItem.position }}</div>
        </div>
      </div>
    </div>

  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, onMounted } from 'vue';
import PageTitle from '../../components/Common/PageTitle.vue';
import protocolSignatoryService, { ProtocolSignatory } from '../../services/protocolSignatoryService';

export default defineComponent({
  name: 'ProtocolSignatoryListPage',
  components: {
    PageTitle,
  },
  setup() {
    const signatories = ref<ProtocolSignatory[]>([]);
    const loading = ref(false);
    const saving = ref(false);
    const actionLoadingId = ref<number | null>(null);
    const searchQuery = ref('');

    // Modal state
    const showModal = ref(false);
    const isEditing = ref(false);
    const editId = ref<number | null>(null);
    const fileInputRef = ref<HTMLInputElement | null>(null);
    const previewUrl = ref<string | null>(null);
    const zoomItem = ref<ProtocolSignatory | null>(null);

    const form = reactive({
      name: '',
      position: '',
      signature_file: null as File | null,
      is_active: true,
      is_default: false,
    });

    const fetchSignatories = async () => {
      loading.value = true;
      try {
        const res = await protocolSignatoryService.getAll({ all: 1 });
        signatories.value = res.data.data || [];
      } catch (err) {
        console.error('Failed to fetch signatories:', err);
      } finally {
        loading.value = false;
      }
    };

    const filteredSignatories = computed(() => {
      if (!searchQuery.value) return signatories.value;
      const q = searchQuery.value.toLowerCase();
      return signatories.value.filter(s =>
        (s.name && s.name.toLowerCase().includes(q)) ||
        (s.position && s.position.toLowerCase().includes(q))
      );
    });

    const openCreateModal = () => {
      isEditing.value = false;
      editId.value = null;
      form.name = '';
      form.position = 'Direktur PT Raya Berdikari Nusantara';
      form.signature_file = null;
      form.is_active = true;
      form.is_default = signatories.value.length === 0;
      previewUrl.value = null;
      if (fileInputRef.value) fileInputRef.value.value = '';
      showModal.value = true;
    };

    const openEditModal = (item: ProtocolSignatory) => {
      isEditing.value = true;
      editId.value = item.id || null;
      form.name = item.name;
      form.position = item.position;
      form.signature_file = null;
      form.is_active = Boolean(item.is_active);
      form.is_default = Boolean(item.is_default);
      previewUrl.value = item.signature_image_url || null;
      if (fileInputRef.value) fileInputRef.value.value = '';
      showModal.value = true;
    };

    const closeModal = () => {
      showModal.value = false;
    };

    const handleFileChange = (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files[0]) {
        const file = target.files[0];
        form.signature_file = file;
        previewUrl.value = URL.createObjectURL(file);
      }
    };

    const saveSignatory = async () => {
      saving.value = true;
      try {
        const formData = new FormData();
        formData.append('name', form.name);
        formData.append('position', form.position);
        formData.append('is_active', form.is_active ? '1' : '0');
        formData.append('is_default', form.is_default ? '1' : '0');

        if (form.signature_file) {
          formData.append('signature_image', form.signature_file);
        }

        if (isEditing.value && editId.value) {
          await protocolSignatoryService.update(editId.value, formData);
        } else {
          await protocolSignatoryService.create(formData);
        }

        closeModal();
        await fetchSignatories();
      } catch (err: any) {
        console.error('Error saving signatory:', err);
        alert(err.response?.data?.message || 'Gagal menyimpan penandatangan.');
      } finally {
        saving.value = false;
      }
    };

    const handleToggleActive = async (item: ProtocolSignatory) => {
      if (!item.id) return;
      actionLoadingId.value = item.id;
      try {
        await protocolSignatoryService.toggleActive(item.id);
        item.is_active = !item.is_active;
      } catch (err: any) {
        console.error('Error toggling active:', err);
        alert(err.response?.data?.message || 'Gagal mengubah status aktif.');
      } finally {
        actionLoadingId.value = null;
      }
    };

    const handleSetDefault = async (item: ProtocolSignatory) => {
      if (!item.id) return;
      actionLoadingId.value = item.id;
      try {
        await protocolSignatoryService.setDefault(item.id);
        await fetchSignatories();
      } catch (err: any) {
        console.error('Error setting default:', err);
        alert(err.response?.data?.message || 'Gagal menetapkan default.');
      } finally {
        actionLoadingId.value = null;
      }
    };

    const handleDelete = async (item: ProtocolSignatory) => {
      if (!item.id) return;
      if (item.is_default) {
        alert('Penandatangan default tidak dapat dihapus. Silakan tetapkan penandatangan lain sebagai default terlebih dahulu.');
        return;
      }
      if (confirm(`Apakah Anda yakin ingin mengarsipkan / menonaktifkan penandatangan "${item.name}"?`)) {
        try {
          await protocolSignatoryService.delete(item.id);
          await fetchSignatories();
        } catch (err: any) {
          console.error('Error deleting signatory:', err);
          alert(err.response?.data?.message || 'Gagal menghapus penandatangan.');
        }
      }
    };

    const zoomSignature = (item: ProtocolSignatory) => {
      zoomItem.value = item;
    };

    const getFileUrl = (path?: string | null) => {
      if (!path) return '';
      if (path.startsWith('blob:') || path.startsWith('data:')) return path;
      if (path.includes('/storage/')) {
        const cleanPath = path.replace(/^https?:\/\/[^/]+\/storage\//, '').replace(/^storage\//, '').replace(/^\//, '');
        const baseUrl = process.env.NODE_ENV === 'production'
          ? 'https://backend-beta.rayacheck.id'
          : 'http://jurnal-backend.test';
        return `${baseUrl}/storage/${cleanPath}`;
      }
      if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
      }
      const cleanPath = path.replace(/^\//, '');
      const baseUrl = process.env.NODE_ENV === 'production'
        ? 'https://backend-beta.rayacheck.id'
        : 'http://jurnal-backend.test';
      return `${baseUrl}/storage/${cleanPath}`;
    };

    const formatDate = (dateStr?: string) => {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      return d.toLocaleDateString('id-ID', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    };

    onMounted(() => {
      fetchSignatories();
    });

    return {
      signatories,
      loading,
      saving,
      actionLoadingId,
      searchQuery,
      filteredSignatories,
      showModal,
      isEditing,
      form,
      previewUrl,
      fileInputRef,
      zoomItem,
      openCreateModal,
      openEditModal,
      closeModal,
      handleFileChange,
      saveSignatory,
      handleToggleActive,
      handleSetDefault,
      handleDelete,
      zoomSignature,
      formatDate,
      getFileUrl,
    };
  }
});
</script>

<style scoped>
.signature-thumb-box {
  width: 100px;
  height: 44px;
  border: 1px dashed #d1d5db;
  border-radius: 6px;
  background-color: #f9fafb;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease;
}
.signature-thumb-box:hover {
  border-color: #6366f1;
  background-color: #eef2ff;
}
.signature-thumb-img {
  max-width: 90%;
  max-height: 80%;
  object-fit: contain;
}
.zoom-badge {
  position: absolute;
  right: 2px;
  bottom: 2px;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  border-radius: 3px;
  padding: 1px 2px;
  opacity: 0;
  transition: opacity 0.2s ease;
}
.signature-thumb-box:hover .zoom-badge {
  opacity: 1;
}

/* Custom Modal Styles */
.custom-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(2px);
  z-index: 1055;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  animation: fadeIn 0.15s ease-out;
}
.custom-modal-card {
  max-width: 580px;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  animation: slideDown 0.2s ease-out;
}
.signature-preview-card {
  min-width: 260px;
  border-radius: 8px;
}
.signature-render-wrap {
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.signature-render-img {
  max-height: 48px;
  max-width: 160px;
  object-fit: contain;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes slideDown {
  from { transform: translateY(-16px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}
</style>
